import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Permission } from '@bp/shared';
import type { ReactNode } from 'react';
import { Layout } from './components/Layout';
import { Empty, Loading } from './components/ui';
import { useAuth } from './lib/auth';
import { LoginPage, AcceptInvitePage, ForgotPasswordPage, ResetPasswordPage } from './pages/auth';
import { OverviewPage } from './pages/Overview';
import { StoresPage } from './pages/Stores';
import { StoreDashboardPage } from './pages/StoreDashboard';
import { AuditsPage } from './pages/Audits';
import { AuditPage } from './pages/Audit';
import { AuditPrintPage } from './pages/AuditPrint';
import { TasksPage } from './pages/Tasks';
import { TaskDetailPage } from './pages/TaskDetail';
import { ComparePage } from './pages/Compare';
import { TemplatesPage } from './pages/Templates';
import { TemplateEditorPage } from './pages/TemplateEditor';
import { UsersPage } from './pages/admin/Users';
import { StoreSetupPage, StoreSetupListPage } from './pages/admin/StoreSetup';
import { ActivityPage } from './pages/admin/Activity';
import { AccountPage } from './pages/Account';

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

function Gate({ perm, children }: { perm: Permission; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(perm)) return <Empty title="You don't have access to this page." />;
  return <>{children}</>;
}

// Store tablets and single-store managers land on their store; everyone else on the overview.
function Home() {
  const { me, can } = useAuth();
  if (!me) return null;
  if (!can('view_dashboards') || (me.store_ids?.length === 1 && me.role !== 'regional_manager')) {
    if (me.store_ids?.length === 1) return <Navigate to={`/stores/${me.store_ids[0]}`} replace />;
    return <Navigate to="/stores" replace />;
  }
  return <OverviewPage />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/audits/:id/print" element={<RequireAuth><AuditPrintPage /></RequireAuth>} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<Home />} />
        <Route path="/overview" element={<Gate perm="view_dashboards"><OverviewPage /></Gate>} />
        <Route path="/stores" element={<StoresPage />} />
        <Route path="/stores/:id" element={<StoreDashboardPage />} />
        <Route path="/audits" element={<AuditsPage />} />
        <Route path="/audits/:id" element={<AuditPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tasks/:id" element={<TaskDetailPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/templates" element={<Gate perm="view_templates"><TemplatesPage /></Gate>} />
        <Route path="/templates/:id" element={<Gate perm="view_templates"><TemplateEditorPage /></Gate>} />
        <Route path="/admin/users" element={<Gate perm="manage_users"><UsersPage /></Gate>} />
        <Route path="/admin/stores" element={<Gate perm="edit_stores"><StoreSetupListPage /></Gate>} />
        <Route path="/admin/stores/:id" element={<Gate perm="edit_stores"><StoreSetupPage /></Gate>} />
        <Route path="/admin/activity" element={<Gate perm="view_activity_log"><ActivityPage /></Gate>} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="*" element={<Empty title="Page not found." />} />
      </Route>
    </Routes>
  );
}
